import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Section, Card, CardHeader, Pill, Stat } from "../components/Primitives";
import { Wrench, ArrowRight, Lightning } from "@phosphor-icons/react";

export default function RegressionOptimizer() {
  const { activeClient, activeClientId } = useClient();
  const [workflows, setWorkflows] = useState([]);
  const [workflowId, setWorkflowId] = useState("");
  const [delta, setDelta] = useState("Refactored allocation engine to use decimal arithmetic for multi-leg orders; updated FX cutoff handler.");
  const [size, setSize] = useState(1200);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeClientId) return;
    setWorkflowId(""); setResult(null);
    api.get("/workflows", { params: { client_id: activeClientId } }).then((r) => {
      setWorkflows(r.data);
      if (r.data.length) setWorkflowId(r.data[0].id);
    });
  }, [activeClientId]);

  async function handleOptimize(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/regression/optimize", {
        workflow_id: workflowId, change_delta: delta, full_suite_size: Number(size),
      });
      setResult(r.data);
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="regression-page">
      <PageHeader
        overline={`Layer 4 · ${activeClient?.name || ""}`}
        title="Regression Optimizer"
        description="Scope regression dynamically based on workflow impact, handoff boundaries, and recent defect vectors."
      />
      <Section>
        <div className="grid grid-cols-12 gap-6">
          <Card className="col-span-12 lg:col-span-5 p-6" testid="regression-form-card">
            <div className="overline text-zinc-500 mb-4">Change Impact</div>
            <form onSubmit={handleOptimize} className="space-y-4" data-testid="regression-form">
              <div>
                <label className="overline text-zinc-500 mb-2 block">Workflow</label>
                <select
                  data-testid="regression-workflow-select"
                  value={workflowId}
                  onChange={(e) => setWorkflowId(e.target.value)}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm bg-white focus:border-vlink-500 outline-none"
                >
                  {workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-zinc-500 mb-2 block">Change delta</label>
                <textarea
                  data-testid="regression-delta-input"
                  value={delta}
                  onChange={(e) => setDelta(e.target.value)}
                  rows={4}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm font-mono bg-white focus:border-vlink-500 outline-none"
                />
              </div>
              <div>
                <label className="overline text-zinc-500 mb-2 block">Full regression suite size</label>
                <input
                  data-testid="regression-size-input"
                  type="number"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm font-mono bg-white focus:border-vlink-500 outline-none"
                />
              </div>
              <button
                data-testid="regression-optimize-btn"
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
              >
                <Wrench size={14} /> {busy ? "Computing scope…" : "Optimize Regression"}
              </button>
            </form>
          </Card>

          <div className="col-span-12 lg:col-span-7">
            {!result ? (
              <Card className="p-12 h-full flex items-center justify-center" testid="regression-empty">
                <div className="text-center max-w-sm">
                  <Lightning size={28} className="mx-auto text-vlink-500" />
                  <div className="mt-4 font-heading text-lg text-zinc-900">Awaiting change delta</div>
                  <p className="mt-2 text-sm text-zinc-600">Submit a delta to compute the minimal regression scope and time saved.</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-6">
                <Card className="p-6" testid="regression-result-card">
                  <CardHeader overline={result.workflow_name} title="Optimized Scope" className="-mx-6 -mt-6 mb-4 px-6" />
                  <div className="grid grid-cols-3 gap-6">
                    <Stat label="Full suite" value={result.full_suite_size} />
                    <Stat label="Scoped" value={result.scoped_size} />
                    <Stat label="Reduction" value={result.reduction_pct} suffix="%" />
                  </div>
                  <div className="mt-6 pt-6 border-t border-zinc-100 flex items-center justify-between">
                    <div>
                      <div className="overline text-zinc-500">Estimated time saved</div>
                      <div className="metric-value text-3xl text-emerald-700 mt-1">
                        {Math.floor(result.saved_minutes / 60)}h {result.saved_minutes % 60}m
                      </div>
                    </div>
                    <Pill tone="success">{result.reduction_pct}% leaner cycle</Pill>
                  </div>
                </Card>

                <Card testid="regression-categories-card">
                  <CardHeader overline="Test scope breakdown" title="By Category" />
                  <div className="px-6 py-4 space-y-3">
                    {result.categories.map((c) => (
                      <div key={c.category} className="flex items-center gap-4 text-sm">
                        <Pill tone={c.priority === "P0" ? "danger" : c.priority === "P1" ? "warn" : "neutral"}>{c.priority}</Pill>
                        <div className="flex-1">{c.category}</div>
                        <div className="font-mono text-zinc-700">{c.count}</div>
                        <div className="w-32 h-1 bg-zinc-100">
                          <div className="h-full bg-vlink-500" style={{ width: `${(c.count / result.scoped_size) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-6" testid="regression-affected-card">
                  <div className="overline text-zinc-500 mb-3">Affected workflow steps</div>
                  <div className="flex flex-wrap gap-2">
                    {result.affected_nodes.map((n) => (
                      <span key={n} className="inline-flex items-center text-xs px-2 py-1 border border-zinc-300 font-mono text-zinc-700">
                        <ArrowRight size={10} className="mr-1 text-vlink-500" /> {n}
                      </span>
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
